import { createSelector } from 'reselect';

// Stable empty objects to prevent infinite re-renders
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

// Base selectors
export const selectClusterState = (state) => state.clusters?.cluster || EMPTY_OBJECT;
export const selectClusters = (state) => state.clusters?.cluster?.clusters || EMPTY_ARRAY;
export const selectSelectedCluster = (state) => state.clusters?.cluster?.selectedCluster || null;

// Existing storage provider selectors
export const selectStorageProviders = (state) => state.clusters?.cluster?.storageProviders || EMPTY_ARRAY;
export const selectStorageProvidersLoading = (state) => state.clusters?.cluster?.storageProvidersLoading || false;
export const selectStorageProvidersError = (state) => state.clusters?.cluster?.storageProvidersError || null;

// VOLUMEZ POLICY CONSTRAINT SELECTORS

// Provider policies selectors
export const selectProviderPolicies = (state) => state.clusters?.cluster?.providerPolicies || EMPTY_OBJECT;
export const selectProviderPoliciesLoading = (state) => state.clusters?.cluster?.providerPoliciesLoading || EMPTY_OBJECT;
export const selectProviderPoliciesError = (state) => state.clusters?.cluster?.providerPoliciesError || EMPTY_OBJECT;

export const selectProviderPoliciesById = createSelector(
  [selectProviderPolicies, (state, providerId) => providerId],
  (providerPolicies, providerId) => providerPolicies[providerId] || EMPTY_ARRAY
);

export const selectProviderPoliciesLoadingById = createSelector(
  [selectProviderPoliciesLoading, (state, providerId) => providerId],
  (loading, providerId) => loading[providerId] || false
);

export const selectProviderPoliciesErrorById = createSelector(
  [selectProviderPoliciesError, (state, providerId) => providerId],
  (errors, providerId) => errors[providerId] || null
);

// Selected policy selectors
export const selectSelectedPolicies = (state) => state.clusters?.cluster?.selectedPolicies || EMPTY_OBJECT;

export const selectSelectedPolicyById = createSelector(
  [selectSelectedPolicies, (state, providerId) => providerId],
  (selectedPolicies, providerId) => selectedPolicies[providerId] || null
);

// Policy constraints selectors
export const selectPolicyConstraints = (state) => state.clusters?.cluster?.policyConstraints || EMPTY_OBJECT;
export const selectPolicyConstraintsLoading = (state) => state.clusters?.cluster?.policyConstraintsLoading || EMPTY_OBJECT;
export const selectPolicyConstraintsError = (state) => state.clusters?.cluster?.policyConstraintsError || EMPTY_OBJECT;

export const selectPolicyConstraintsByProvider = createSelector(
  [selectPolicyConstraints, (state, providerId) => providerId],
  (policyConstraints, providerId) => policyConstraints[providerId] || EMPTY_OBJECT
);

export const selectPolicyConstraintsByProviderAndPolicy = createSelector(
  [selectProviderPoliciesById, (state, providerId, policyName) => policyName],
  (providerPolicies, policyName) => {
    
    if (!providerPolicies || !policyName) return null;
    
    // Find the policy by name in the provider's policies array
    const policy = providerPolicies.find(p => p.name === policyName);
    if (!policy) {
      return null;
    }
    
    
    // Return the policy with allowedConfigurations as constraints
    return {
      allowedConfigurations: policy.allowedConfigurations || [],
      ...policy // Include all policy data
    };
  }
);

export const selectPolicyConstraintsLoadingByProvider = createSelector(
  [selectPolicyConstraintsLoading, (state, providerId) => providerId],
  (loading, providerId) => loading[providerId] || EMPTY_OBJECT
);

export const selectPolicyConstraintsErrorByProvider = createSelector(
  [selectPolicyConstraintsError, (state, providerId) => providerId],
  (errors, providerId) => errors[providerId] || EMPTY_OBJECT
);

// Configuration validation selectors
export const selectConfigurationValidation = (state) => state.clusters?.cluster?.configurationValidation || EMPTY_OBJECT;
export const selectConfigurationValidationLoading = (state) => state.clusters?.cluster?.configurationValidationLoading || EMPTY_OBJECT;

export const selectConfigurationValidationById = createSelector(
  [selectConfigurationValidation, (state, providerId) => providerId],
  (validation, providerId) => validation[providerId] || null
);

export const selectConfigurationValidationLoadingById = createSelector(
  [selectConfigurationValidationLoading, (state, providerId) => providerId],
  (loading, providerId) => loading[providerId] || false
);

// COMPUTED SELECTORS FOR UI COMPONENTS

// Get filtered instance types based on selected policy constraints
export const selectFilteredInstanceTypes = createSelector(
  [
    selectPolicyConstraintsByProviderAndPolicy,
    (state, providerId, policyName, availableInstances) => availableInstances || []
  ],
  (constraints, availableInstances) => {
    if (!constraints || !constraints.allowedConfigurations) {
      return availableInstances;
    }

    // Filter instances based on policy constraints
    const allowedInstanceTypes = constraints.allowedConfigurations.map(config => config.instanceType);
    return availableInstances.filter(instance => 
      allowedInstanceTypes.includes(instance.type) || allowedInstanceTypes.includes(instance.name)
    );
  }
);

// Get allowed volume sizes for a configuration
export const selectAllowedVolumeSizes = createSelector(
  [
    selectPolicyConstraintsByProviderAndPolicy,
    (state, providerId, policyName, instanceType) => instanceType
  ],
  (constraints, instanceType) => {
    if (!constraints || !constraints.allowedConfigurations) {
      return null;
    }

    const config = constraints.allowedConfigurations.find(
      config => config.instanceType === instanceType
    );
    
    return config ? config.allowedVolumeSizes : null;
  }
);

// Check if current configuration is valid
export const selectConfigurationIsValid = createSelector(
  [selectConfigurationValidationById],
  (validation) => {
    if (!validation) return null;
    return validation.isValid === true;
  }
);

// Get validation errors for current configuration
export const selectValidationErrors = createSelector(
  [selectConfigurationValidationById],
  (validation) => {
    if (!validation || validation.isValid) return [];
    return validation.errors || [];
  }
);

// Combined selector for policy UI state
export const selectPolicyUIState = createSelector(
  [
    selectProviderPoliciesById,
    selectProviderPoliciesLoadingById,
    selectProviderPoliciesErrorById,
    selectSelectedPolicyById,
    selectPolicyConstraintsByProvider,
    selectConfigurationValidationById,
    selectConfigurationValidationLoadingById,
    (state, providerId) => providerId
  ],
  (policies, loading, error, selectedPolicy, constraints, validation, validationLoading, providerId) => ({
    providerId,
    policies,
    loading,
    error,
    selectedPolicy,
    constraints,
    validation,
    validationLoading,
    hasValidSelection: selectedPolicy && constraints[selectedPolicy],
    isConfigurationValid: validation?.isValid === true
  })
);

// Selector to check if policy data is ready for a provider
export const selectPolicyDataReady = createSelector(
  [
    selectProviderPoliciesById,
    selectProviderPoliciesLoadingById,
    selectProviderPoliciesErrorById
  ],
  (policies, loading, error) => {
    return !loading && !error && policies && policies.length > 0;
  }
);
